import api from '@cannect/services/api'
import {
  TAvailabilityResponse,
  TCallConnectionPayload,
  TCallStateResponse,
  TCouponValidateBody,
  TCouponValidateResponse,
  TFinishSessionResponse,
  TGenerateTokenResponse,
  TGetoutQueueResponse,
  TGetSessionResponse,
  TNotificationStartResponse,
  TPathology,
  TPaymentStatusResponse,
  TPostTriageAnswerPayload,
  TRateResponse,
  TSessionAssignment,
  TSessionCall,
  TSessionCallListItem,
  TSessionPaymentPayload,
  TSessionPaymentResponse,
  TStartSessionResponse,
  TTriageQuestion
} from './types'

const ALLOWED_PATHOLOGY_NAMES = [
  'Dor Crônica',
  'Ansiedade',
  'Burnout',
  'Depressão',
  'Insônia',
  'Autismo',
  'Epilepsia',
  'Parkinson',
  'Demências',
  'Autismo',
  'Outros'
] as const

function orderPathologiesLikeApp(list: TPathology[]): TPathology[] {
  return [...list].sort((a, b) => {
    if (a.name === 'Outros') return 1
    if (b.name === 'Outros') return -1
    return 0
  })
}

export async function getPathologiesForSessionCall(): Promise<TPathology[]> {
  const res = await api.get<{ success: boolean; pathologies: TPathology[] }>('/pathologies/all')
  const raw = res.data.pathologies ?? []
  const filtered = raw.filter((p) =>
    ALLOWED_PATHOLOGY_NAMES.includes(p.name as (typeof ALLOWED_PATHOLOGY_NAMES)[number])
  )
  return orderPathologiesLikeApp(filtered)
}

export const getPathologiesOptions = async (): Promise<{ pathologies: TPathology[] }> => {
  return api
    .get<{ success: boolean; pathologies: TPathology[] }>('/scheduling/filters/pathologies')
    .then((res) => res.data)
}

// ── Disponibilidade ───────────────────────────────────────────────────────

/** GET /app-availability — gate de disponibilidade (sem auth). */
export const getAppAvailability = async (): Promise<TAvailabilityResponse> => {
  return api.get<TAvailabilityResponse>('/app-availability').then((res) => res.data)
}

// ── Criação de sessão / triagem ─────────────────────────────────────────────

/**
 * POST /generate-token — origem web sempre 'site'.
 * origin (opcional) marca a sessão como whitelabel (ex.: 'remederi');
 * o backend ignora valores fora do mapa do piloto.
 */
export const createInstantSession = async (origin?: string | null): Promise<TGenerateTokenResponse> => {
  return api
    .post<TGenerateTokenResponse>('/generate-token', {
      consultation_origin: 'site',
      ...(origin ? { origin } : {})
    })
    .then((res) => res.data)
}

export const getTriageQuestions = async (): Promise<TTriageQuestion[]> => {
  const res = await api.get<TTriageQuestion[]>('/questions')
  return res.data
}

export const postTriageAnswer = async (
  questionId: number,
  body: Omit<TPostTriageAnswerPayload, 'questionId'>
): Promise<unknown> => {
  const res = await api.post(`/answers/${questionId}`, {
    questionId,
    ...body
  })
  return res.data
}

/**
 * POST /set-session-pathologies — body: { session_id, pathologies }.
 * NB: o backend lê `pathologies` (não `pathology_ids` como diz o contrato escrito);
 * enviar `pathology_ids` causa "pathologies is not iterable".
 */
export const setSessionPathologies = async (
  sessionId: number,
  pathologyIds: number[]
): Promise<{ success: boolean }> => {
  return api
    .post<{ success: boolean }>('/set-session-pathologies', {
      session_id: sessionId,
      pathologies: pathologyIds
    })
    .then((res) => res.data)
}

// ── Pagamento / cupom ────────────────────────────────────────────────────────

/** POST /session/payment/cupom — valida cupom e devolve amount/discount. */
export const validateCoupon = async (body: TCouponValidateBody): Promise<TCouponValidateResponse> => {
  return api.post<TCouponValidateResponse>('/session/payment/cupom', body).then((res) => res.data)
}

/** POST /session/payment — payload v1 com card_token (nunca cartão cru). */
export const payInstantSession = async (payload: TSessionPaymentPayload): Promise<TSessionPaymentResponse> => {
  return api.post<TSessionPaymentResponse>('/session/payment', payload).then((res) => res.data)
}

/** GET /session/get-status-payment/:id — polling de status (sem auth). */
export const getPaymentStatus = async (id: number): Promise<TPaymentStatusResponse> => {
  return api.get<TPaymentStatusResponse>(`/session/get-status-payment/${id}`).then((res) => res.data)
}

// ── Fila / start / atribuição ────────────────────────────────────────────────

/** PUT /session/start/:uuid — entra na fila. */
export const startInstantSession = async (uuid: string): Promise<TStartSessionResponse> => {
  return api.put<TStartSessionResponse>(`/session/start/${uuid}`).then((res) => res.data)
}

/** POST /session/notification-start/:uuid — notifica médicos disponíveis. */
export const notifyDoctors = async (uuid: string): Promise<TNotificationStartResponse> => {
  return api.post<TNotificationStartResponse>(`/session/notification-start/${uuid}`).then((res) => res.data)
}

/** PUT /session/getout-queue/:uuid — sair da fila. */
export const getoutQueue = async (uuid: string): Promise<TGetoutQueueResponse> => {
  return api.put<TGetoutQueueResponse>(`/session/getout-queue/${uuid}`).then((res) => res.data)
}

/** GET /get-session-by-id/:id — polling de atribuição do médico (campos Daily). */
export const getSessionByIdForAssignment = async (id: number): Promise<TSessionAssignment> => {
  return api.get<TSessionAssignment>(`/get-session-by-id/${id}`).then((res) => res.data)
}

// ── Estado da chamada ────────────────────────────────────────────────────────

export const updateConnectionStatus = async (
  uuid: string,
  payload: TCallConnectionPayload
): Promise<TCallStateResponse> => {
  return api.put<TCallStateResponse>(`/session/call-state/${uuid}/connection`, payload).then((res) => res.data)
}

export const getCallState = async (uuid: string): Promise<TCallStateResponse> => {
  return api.get<TCallStateResponse>(`/session/call-state/${uuid}`).then((res) => res.data)
}

// ── Conclusão / pós-consulta ─────────────────────────────────────────────────

export const finishInstantSession = async (
  id: number,
  endedBy: 'patient' | 'doctor'
): Promise<TFinishSessionResponse> => {
  return api.put<TFinishSessionResponse>(`/finish-session/${id}`, { endedBy }).then((res) => res.data)
}

/** POST /session/rate/:id — avaliação 1..5. */
export const rateInstantSession = async (id: number, rating: number): Promise<TRateResponse> => {
  return api.post<TRateResponse>(`/session/rate/${id}`, { rating }).then((res) => res.data)
}

/** GET /get-one-session-user/:id — sessão + receita + anotações. */
export const getSessionById = async (id: number): Promise<TGetSessionResponse> => {
  return api.get<TGetSessionResponse>(`/get-one-session-user/${id}`).then((res) => res.data)
}

export async function getSessionCallsByUser(params?: {
  limit?: number
  page?: number
}): Promise<TSessionCallListItem[]> {
  const res = await api.get<TSessionCallListItem[]>('/get-all-session-call-by-user', { params })
  return Array.isArray(res.data) ? res.data : []
}

// ── Helpers tolerantes (variação de nome entre versões) ───────────────────────

/** Extrai a URL da sala Daily de uma sessão/atribuição, tolerando snake_case/camelCase. */
export function getDailyRoomUrl(
  source: Partial<TSessionCall & TSessionAssignment> & Record<string, unknown>
): string | null {
  return (
    (source?.dailyRoomUrl as string | null | undefined) ?? (source?.daily_room_url as string | null | undefined) ?? null
  )
}

/** Extrai o token (viewer) Daily de uma sessão/atribuição, tolerando snake_case/camelCase. */
export function getDailyToken(
  source: Partial<TSessionCall & TSessionAssignment> & Record<string, unknown>
): string | null {
  return (
    (source?.dailyMeetingToken as string | null | undefined) ??
    (source?.daily_meeting_token as string | null | undefined) ??
    null
  )
}

/** Normaliza uma sessão crua garantindo campos Daily presentes (null quando ausentes). */
export function normalizeSessionCall<T extends Partial<TSessionCall> & Record<string, unknown>>(
  raw: T
): T & { dailyRoomUrl: string | null; dailyMeetingToken: string | null } {
  return {
    ...raw,
    dailyRoomUrl: getDailyRoomUrl(raw),
    dailyMeetingToken: getDailyToken(raw)
  }
}

export function normalizeSessionCallListItem(raw: TSessionCallListItem): TSessionCallListItem {
  return normalizeSessionCall(raw)
}
